import { BASE_BACKEND_URL } from '@/requests/constants';
import { getApi } from '@/requests/api';
import { DateTime } from 'luxon';

import { useManyPerms } from '@/hooks/usePerms';
import { useSession } from '@/providers/session-provider';
import type {
  QueryFunctionContext,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

type FailedInvoiceIds = Array<{ object_id: string }>;
type CountResponse = { count: number; identifier?: string };

type FailedInvoicesResult<T> = Omit<UseQueryResult<T, Error>, 'data'> & {
  data: T | undefined;
  canView: boolean;
};

export function useCanViewFailedInvoices() {
  return useManyPerms(['invoice', 'notification', 'notificationdistribution'])
    .canViewAll;
}

export function useFailedInvoices(): FailedInvoicesResult<FailedInvoiceIds>;
export function useFailedInvoices(
  countOnly: true
): FailedInvoicesResult<CountResponse>;
export function useFailedInvoices(
  countOnly?: boolean
): FailedInvoicesResult<FailedInvoiceIds | CountResponse> {
  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const canView = useCanViewFailedInvoices();

  const query = useQuery(
    failedInvoices({ countOnly, organizationId, enabled: canView })
  );
  return {
    ...query,
    canView,
    data: canView && !query.isError ? query.data : undefined,
    isLoading: canView && query.isLoading,
  };
}

export function failedInvoices({
  countOnly,
  identifier = '',
  enabled,
  organizationId,
}: {
  countOnly?: boolean;
  organizationId?: string;
  identifier?: string;
  enabled?: boolean;
}) {
  return {
    queryKey: [
      'failedInvoices',
      countOnly,
      organizationId ? { organization: organizationId } : undefined,
      identifier,
    ],
    queryFn: async ({
      signal,
    }: QueryFunctionContext): Promise<FailedInvoiceIds | CountResponse> => {
      const params = {
        ...(organizationId ? { organization: organizationId } : {}),
        content_type: 'accounting.invoice',
        start_time: DateTime.now()
          .minus({ month: 1 })
          .startOf('day')
          .toISODate(),
        end_time: DateTime.now().plus({ days: 1 }).startOf('day').toISODate(),
        event_identifier: 'accounting.invoice.invoiceattested',
        object_filter_data: JSON.stringify({
          closed_time__isnull: true,
          due_date__gte: DateTime.now().toISODate(),
        }),
      };

      const data = await getApi<FailedInvoiceIds>({
        url:
          `${BASE_BACKEND_URL}/communication/failed_notification_deliveries/?` +
          new URLSearchParams(params).toString(),
        signal,
        errorMessage: 'Failed to fetch failed deliveries',
      });

      if (!countOnly) {
        return data;
      }
      return {
        identifier: identifier || undefined,
        count: data.length,
      } as CountResponse;
    },
    enabled,
  } as const;
}

export function failedInvoicesCountQueryOptions({
  countOnly,
  identifier = '',
  enabled,
  organizationId,
}: {
  countOnly?: boolean;
  organizationId?: string;
  identifier?: string;
  enabled?: boolean;
}): UseQueryOptions<CountResponse> {
  return {
    queryKey: [
      'failedInvoices',
      countOnly,
      organizationId ? { organization: organizationId } : undefined,
      identifier,
    ],
    queryFn: async (context): Promise<CountResponse> => {
      return (await failedInvoices({
        countOnly,
        identifier,
        enabled,
        organizationId,
      }).queryFn(context)) as CountResponse;
    },
    enabled,
  } as const;
}
